import React, { useState, useEffect, useMemo } from 'react';
import { MapPin, Calendar, Plane, Hotel, Utensils, Send, Loader, Key, AlertCircle, Eye, EyeOff, X, ExternalLink, ChevronDown, ChevronRight, ChevronUp, Trophy, Clock, Star } from 'lucide-react';
import { generateTravelPlan, parseAIResponse, isOpenAIConfigured, getAPIKeyInstructions } from '../utils/openai';
import { getUpcomingGames } from '../data/schedules';
import { useTheme } from '../contexts/ThemeContext';
import EnhancedBookingModal from './EnhancedBookingModal';
import { generateSmartBookingUrls, generateRestaurantUrl } from '../utils/bookingUrls';
import { trackAffiliateClick } from '../utils/analytics';
import { logEvent } from '../utils/analytics';
import TabbedItineraryModal from './TabbedItineraryModal';

// Modal component for booking links
const BookingModal = ({ isOpen, onClose, title, url, partner }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <div className="flex items-center space-x-4">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 text-sm"
            >
              <ExternalLink size={16} />
              <span>Open in New Tab</span>
            </a>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 flex items-center justify-center bg-gray-50">
          <div className="text-center p-8">
            <div className="text-6xl mb-4">🚀</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Redirecting to {partner}...</h3>
            <p className="text-gray-600 mb-4">Your search has been pre-filled with game details!</p>
            <button
              onClick={() => {
                window.open(url, '_blank', 'noopener,noreferrer');
                onClose();
              }}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Open {partner} Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Game card component for schedule display
const GameCard = ({ game, teamColors, onPlanTrip, isExpanded, onToggle, isLoading }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    
    try {
      // If dateString is in YYYY-MM-DD format, parse it safely
      // Add 'T12:00:00' to avoid timezone shift issues with date-only strings
      let date;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        // Date-only string - append noon to avoid timezone issues
        date = new Date(dateString + 'T12:00:00');
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        return 'TBD';
      }
      
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return 'TBD';
    }
  };

  const isRivalryGame = game.rivalry || game.specialGame;
  const isHomeGame = game.location === 'home';

  return (
    <div className={`border rounded-xl transition-all duration-200 ${
      isExpanded ? 'border-blue-400 shadow-lg' : 'border-gray-200 hover:border-gray-300'
    }`}>
      <button
        onClick={onToggle}
        className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 rounded-xl transition-colors"
      >
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: isHomeGame ? teamColors.primary : teamColors.secondary }}
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="text-left">
              <div className="flex items-center space-x-2">
                <p className="font-semibold text-gray-900">{game.opponent}</p>
                {isRivalryGame && <Star size={16} className="text-yellow-500" />}
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span>{formatDate(game.date)}</span>
                {game.kickoff && game.kickoff !== 'TBD' && game.kickoff !== '12:00 AM' && !game.kickoff.includes('12:00 AM') ? (
                  <span className="flex items-center space-x-1">
                    <Clock size={14} />
                    <span>{game.kickoff}</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-1 text-gray-400">
                    <Clock size={14} />
                    <span>TBD</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="text-right">
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            isHomeGame 
              ? 'bg-green-100 text-green-800' 
              : game.location === 'neutral' 
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
          }`}>
            {isHomeGame ? 'HOME' : game.location === 'neutral' ? 'NEUTRAL' : 'AWAY'}
          </div>
          {game.specialGame && (
            <p className="text-xs text-purple-600 font-medium mt-1">{game.specialGame}</p>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Venue:</span>
                <p className="font-medium">{game.venue}</p>
              </div>
              <div>
                <span className="text-gray-600">Week:</span>
                <p className="font-medium">Week {game.week}</p>
              </div>
            </div>
            
            {game.specialGame && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-purple-800 text-sm font-medium">{game.specialGame}</p>
                <p className="text-purple-700 text-xs mt-1">This is a special rivalry game with extra atmosphere!</p>
              </div>
            )}

            <button
              onClick={() => onPlanTrip(game)}
              disabled={isLoading}
              className="w-full mt-4 py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              style={{ 
                background: `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`, color: '#ffffff', textShadow: '0 1px 1px rgba(0,0,0,0.25)'
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <Loader className="animate-spin" size={20} />
                  <span>Planning Your Trip...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <MapPin size={20} />
                  <span>Plan Trip for This Game</span>
                </div>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SportsAssistant = () => {
  const { selectedTeam, teamColors } = useTheme();
  const [formData, setFormData] = useState({
    travelFrom: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStartedAt, setLoadingStartedAt] = useState(null);
  const [loadingSecondsLeft, setLoadingSecondsLeft] = useState(20);
  const [recommendation, setRecommendation] = useState(null);
  const [showAPIInstructions, setShowAPIInstructions] = useState(false);
  const [showRawResponse, setShowRawResponse] = useState(false);
  const [error, setError] = useState(null);
  const [fromToast, setFromToast] = useState('');
  const [modalData, setModalData] = useState({ isOpen: false, title: '', url: '', partner: '' });
  const [expandedGame, setExpandedGame] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [enhancedModalData, setEnhancedModalData] = useState({ 
    isOpen: false, 
    bookingType: '', 
    game: null, 
    teamName: '' 
  });
  const [teamSchedule, setTeamSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [showAllMatchups, setShowAllMatchups] = useState(false);
  const [showSeason, setShowSeason] = useState(false);
  const [showTabbedModal, setShowTabbedModal] = useState(false);

  // Fetch team schedule using real API
  useEffect(() => {
    const fetchSchedule = async () => {
      if (!selectedTeam) {
        setTeamSchedule(null);
        return;
      }
      setScheduleLoading(true);
      setScheduleError(null);

      try {
        const schedule = await getUpcomingGames(selectedTeam.name, 6);
        console.log('🏈 SportsAssistant received schedule:', schedule);
        setTeamSchedule(schedule);
      } catch (error) {
        console.error('❌ Error fetching team schedule:', error);
        setScheduleError('Unable to load schedule. Please try again.');
        setTeamSchedule(null);
      } finally {
        setScheduleLoading(false);
      }
    };

    fetchSchedule();
  }, [selectedTeam]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const openBookingModal = (title, url, partner) => {
    setModalData({ isOpen: true, title, url, partner });
  };

  const closeBookingModal = () => {
    setModalData({ isOpen: false, title: '', url: '', partner: '' });
  };

  const openEnhancedBookingModal = (bookingType, game, teamName) => {
    // Temporarily disabled - modals hidden for now
    console.log('Booking modal disabled:', bookingType);
    return;
    
    // setEnhancedModalData({ 
    //   isOpen: true, 
    //   bookingType, 
    //   game, 
    //   teamName: teamName || selectedTeam?.name || '' 
    // });

    // try {
    //   const urls = generateSmartBookingUrls(game, teamName || selectedTeam?.name || '', formData.travelFrom || '');
    //   const url = urls?.[bookingType];
    //   if (url) {
    //     try { trackAffiliateClick(bookingType, url, { context: 'sports_assistant' }); } catch {}
    //     window.open(url, '_blank', 'noopener,noreferrer');
    //   }
    // } catch {}
  };

  const closeEnhancedBookingModal = () => {
    setEnhancedModalData({ 
      isOpen: false, 
      bookingType: '', 
      game: null, 
      teamName: '' 
    });
  };

  const handleGameExpand = (gameIndex) => {
    setExpandedGame(expandedGame === gameIndex ? null : gameIndex);
  };

  const handlePlanTrip = async (game) => {
    if (!formData.travelFrom) {
      setFromToast('Tell us where you\'re starting from.');
      // Auto-hide toast after 3 seconds
      setTimeout(() => setFromToast(''), 3000);
      const input = document.getElementById('travelFrom');
      if (input) input.focus();
      return;
    }

    try { logEvent('plan_trip_click', { team: selectedTeam?.name, opponent: game?.opponent, date: game?.date }); } catch {}
    setSelectedGame(game);
    setLoadingStartedAt(Date.now());
    setLoadingSecondsLeft(30);
    setIsLoading(true);
    setError(null);
    setRecommendation(null);
    
    try {
      if (isOpenAIConfigured()) {
        console.log('Generating travel plan with AI...');
        // Use real OpenAI API
        const aiResponse = await generateTravelPlan(
          { 
            game: `${selectedTeam.name} vs ${game.opponent}`, 
            date: game.date,
            venue: game.venue,
            kickoff: game.kickoff,
            isRivalry: game.rivalry || game.specialGame,
            specialGame: game.specialGame
          },
          formData.travelFrom,
          selectedTeam
        );
        
        console.log('AI Response received, parsing...');
        const parsedResponse = parseAIResponse(aiResponse);
        console.log('Final parsed response:', parsedResponse);
        setRecommendation(parsedResponse);
        setShowTabbedModal(true);
        try { logEvent('itinerary_generated', { team: selectedTeam?.name, opponent: game?.opponent }); } catch {}
      } else {
        // Enhanced mock data with game-specific information
        // Determine team type for tailored messaging
        const nflTeams = [
          "Arizona Cardinals", "Atlanta Falcons", "Baltimore Ravens", "Buffalo Bills", "Carolina Panthers", 
          "Chicago Bears", "Cincinnati Bengals", "Cleveland Browns", "Dallas Cowboys", "Denver Broncos",
          "Detroit Lions", "Green Bay Packers", "Houston Texans", "Indianapolis Colts", "Jacksonville Jaguars",
          "Kansas City Chiefs", "Las Vegas Raiders", "Los Angeles Chargers", "Los Angeles Rams", "Miami Dolphins",
          "Minnesota Vikings", "New England Patriots", "New Orleans Saints", "New York Giants", "New York Jets",
          "Philadelphia Eagles", "Pittsburgh Steelers", "San Francisco 49ers", "Seattle Seahawks", 
          "Tampa Bay Buccaneers", "Tennessee Titans", "Washington Commanders"
        ];
        const isNFL = nflTeams.some(nflTeam => selectedTeam.name.includes(nflTeam));
        const teamType = isNFL ? 'NFL' : 'College';
        
        const mockResponse = {
          scheduleVerification: {
            gameExists: true,
            actualDate: game.date,
            venue: game.venue,
            notes: `✅ ${selectedTeam.name} vs ${game.opponent} confirmed for ${game.date} ${isNFL ? '(NFL Schedule)' : '(College Schedule)'}`
          },
          gameInfo: {
            matchup: `${selectedTeam.name} vs ${game.opponent}`,
            date: game.date,
            venue: game.venue,
            kickoff: game.kickoff,
            atmosphere: game.specialGame 
              ? `🔥 This is the ${game.specialGame}! ${isNFL ? 'One of the biggest NFL rivalry games with incredible professional atmosphere and playoff implications.' : 'One of the biggest college rivalry games of the year with incredible campus atmosphere and tradition.'}` 
              : `This ${game.location === 'home' ? 'home' : 'away'} game against ${game.opponent} promises great action. ${isNFL ? 'Perfect NFL matchup with professional atmosphere.' : 'Classic college football with campus energy.'} ${game.rivalry ? 'This is a rivalry matchup with extra intensity!' : ''}`
          },
          travel: {
            flights: [
              {
                suggestion: `Smart booking for ${game.venue.split(',').pop().trim()} - book early for this ${game.specialGame ? 'huge rivalry game' : 'important matchup'}`,
                link: "https://www.expedia.com/?affcid=EXPEDIA.AFFILIATE.40BAGS",
                partner: "Expedia",
                details: "AI would provide optimal flight times and cost-saving tips specific to this destination"
              },
              {
                suggestion: "Alternative routing and airport options for the best deals",
                link: "https://www.travelocity.com/?affid=40bags",
                partner: "Travelocity",
                details: "AI would suggest connecting flight benefits and budget strategies"
              }
            ],
            hotels: [
              {
                name: `${teamType} Game Day Experience Hotels`,
                suggestion: `THE hotels where ${selectedTeam.name} fans ${isNFL ? 'and corporate groups' : 'and alumni'} stay for ${game.opponent} games - these places will be buzzing with ${game.specialGame ? 'rivalry' : 'game'} energy! Picture ${isNFL ? 'business networking mixed with fan passion' : 'waking up to fight songs in the lobby'}.`,
                link: "https://www.hotels.com/?affid=40bags",
                partner: "Hotels.com",
                details: `AI would list specific hotels near ${game.venue} with ${isNFL ? 'corporate packages and premium shuttles' : 'campus shuttles and alumni discounts'} for ${game.specialGame ? 'rivalry weekend' : 'game-day'} packages.`,
                atmosphere: `Electric ${selectedTeam.name} energy, ${isNFL ? 'professional networking atmosphere' : 'college spirit in the lobby'}, fellow fans as neighbors`
              },
              {
                name: `Luxury ${teamType} Weekend Escapes`,
                suggestion: `Treat this ${game.specialGame ? 'legendary rivalry game' : `${teamType} trip`} like the special occasion it is! Perfect for celebrating ${selectedTeam.name} ${game.location === 'home' ? 'at home' : 'on the road'} ${isNFL ? 'in professional style' : 'with alma mater pride'}.`,
                link: "https://www.booking.com/?aid=40bags",
                partner: "Booking.com",
                details: `Premium hotels near ${game.venue.split(',')[0]} with ${isNFL ? 'downtown locations and executive lounges' : 'historic charm and campus views'}.`,
                atmosphere: `Sophisticated comfort, ${isNFL ? 'city skylines' : 'campus nostalgia'}, perfect for making this trip memorable`
              },
              {
                name: `${teamType} Fan Gathering Houses`,
                suggestion: `Turn your ${game.opponent} trip into an epic ${selectedTeam.name} ${isNFL ? 'fan experience with fellow professionals' : 'alumni reunion'}! Perfect for groups traveling to this ${game.specialGame ? 'rivalry showdown' : 'away game'}.`,
                link: "https://www.vrbo.com/?affiliateId=40bags",
                partner: "VRBO",
                details: `Houses perfect for ${isNFL ? 'corporate tailgating, premium grilling, and RedZone watching' : 'alma mater gatherings, pregame cooking, and watching other college games'}.`,
                atmosphere: `${isNFL ? 'Professional tailgate atmosphere, networking central' : 'College nostalgia central, alumni bonding'}, memories in the making`
              }
            ]
          },
          dining: [
            {
              category: `Legendary Pre-Game Spots in ${game.venue.split(',')[0]}`,
              description: `These aren't just restaurants - they're ${isNFL ? 'institutions where NFL fans and business groups gather before professional games' : 'where college fans and alumni gather before taking on the opposition'}! ${game.specialGame ? `${isNFL ? 'NFL rivalry traditions and corporate networking run deep here.' : 'College rivalry game traditions run deep here.'}` : `Every true ${teamType} fan needs to experience these places.`}`,
              venues: [
                `AI would find the legendary ${game.venue.split(',')[0]} spot where ${selectedTeam.name} fans congregate - The story of ${isNFL ? 'professional football traditions and decades of NFL fan culture' : 'game day traditions and decades of college fan gatherings'}`,
                `Iconic pre-game institution near ${game.venue} - The experience you'll have here, famous ${isNFL ? 'NFL' : 'college'} moments, and must-try items that locals swear by`,
                `Local ${game.opponent} area tradition - How this place fits into the ${isNFL ? 'professional game culture and business networking' : 'college rivalry culture'}, secret menu items, and optimal arrival times`
              ]
            },
            {
              category: `Victory (or Recovery) Dining After the ${game.opponent} Game`,
              description: `Win or lose against ${game.opponent}, you'll need the perfect spot to either celebrate ${selectedTeam.name} victory or comfort your soul. ${game.specialGame ? `These places understand the emotional intensity of ${isNFL ? 'NFL rivalry games and professional competition' : 'college rivalry games and alma mater pride'}.` : `These places understand the rollercoaster of ${teamType} game day.`}`,
              venues: [
                `AI would recommend the ${selectedTeam.name} celebration central - Where champions go after beating ${game.opponent}, ${isNFL ? 'perfect for corporate celebrations and professional networking' : 'with late hours and highlight reels'}`,
                `Comfort food haven for tough losses - The spot that understands when ${game.opponent} gets the better of ${selectedTeam.name}, ${isNFL ? 'where business groups process tough defeats' : 'comforting generations of heartbroken alumni'}`,
                `Late night legend - Perfect for rehashing every play of the ${selectedTeam.name} vs ${game.opponent} game ${isNFL ? 'until the business networking winds down' : 'until the conversations run out'}`
              ]
            }
          ],
          tickets: [
            {
              suggestion: `Get verified tickets for ${selectedTeam.name} vs ${game.opponent} ${game.specialGame ? `- this ${isNFL ? 'NFL rivalry game' : 'college rivalry game'} will sell out fast!` : `with insider ${teamType} seating advice`}`,
              link: "https://www.stubhub.com/?gcid=chAFF-_-geoUS-_-genTICKETS-_-dt170201-_-40bags",
              partner: "StubHub",
              details: `AI would suggest best sections at ${game.venue} for ${selectedTeam.name} fans, ${isNFL ? 'corporate packages and premium seating options' : 'student section proximity and alumni sections'}, price ranges, and timing strategies for this ${game.specialGame ? 'rivalry' : ''} matchup`
            },
            {
              suggestion: `Official ${isNFL ? 'NFL' : selectedTeam.name} marketplace - ${isNFL ? 'season ticket holder resales' : 'face value opportunities'} for ${game.opponent} game`,
              link: "https://www.ticketmaster.com/affiliates/?tm_link=40bags",
              partner: "Ticketmaster",
              details: `AI would identify ${isNFL ? 'official NFL pricing, season ticket holder resale options, and corporate packages' : 'official release dates, student ticket policies, and alumni allotments'} for this specific game`
            }
          ],
          usingMockData: true,
          aiNote: `This is enhanced sample data tailored to your selected ${teamType} game. Connect your OpenAI API key for live ${isNFL ? 'NFL' : 'college football'} schedule verification, specific venue recommendations, and insider travel tips optimized for ${teamType} fan culture!`
        };
        
        setRecommendation(mockResponse);
        setShowTabbedModal(true);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error generating travel plan:', err);
    } finally {
      setIsLoading(false);
      setLoadingStartedAt(null);
    }
  };

  const primaryColor = teamColors?.primary || '#0ea5e9';
  const secondaryColor = teamColors?.secondary || '#f97316';

  // Loading countdown/progress ring updater
  useEffect(() => {
    if (!isLoading || !loadingStartedAt) return;
    let rafId;
    let timerId;

    const update = () => {
      const elapsed = (Date.now() - loadingStartedAt) / 1000;
      const remaining = Math.max(0, 30 - Math.floor(elapsed));
      setLoadingSecondsLeft(remaining);
      if (isLoading) {
        rafId = requestAnimationFrame(update);
      }
    };
    // Start both rAF (smooth) and a 250ms backup interval for responsiveness
    rafId = requestAnimationFrame(update);
    timerId = setInterval(update, 250);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (timerId) clearInterval(timerId);
    };
  }, [isLoading, loadingStartedAt]);

  // Close loading modal immediately when results are ready
  useEffect(() => {
    if (!isLoading && loadingStartedAt) {
      // Results are ready, close the loading modal immediately
      setLoadingStartedAt(null);
      setLoadingSecondsLeft(0);
    }
  }, [isLoading, loadingStartedAt]);

  const loadingProgress = useMemo(() => {
    const elapsed = loadingStartedAt ? (Date.now() - loadingStartedAt) / 1000 : 0;
    const pct = Math.min(1, Math.max(0, elapsed / 30));
    return pct;
  }, [loadingStartedAt]);

  // Show team selection prompt if no team is selected
  if (!selectedTeam) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card p-8 bg-white text-center">
          <div 
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
            style={{ 
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` 
            }}
          >
            <Trophy size={32} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Select Your Team First</h2>
          <p className="text-xl text-gray-600 mb-6">Choose your team to see their schedule and plan trips to games!</p>
          <button 
            onClick={() => window.location.reload()} // This will trigger the team selection modal
            className="inline-flex items-center space-x-2 px-8 py-4 rounded-xl font-semibold text-white transition-all duration-200 transform hover:scale-105"
            style={{ 
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` 
            }}
          >
            <Trophy size={20} />
            <span>Choose Your Team</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <BookingModal
        isOpen={modalData.isOpen}
        onClose={closeBookingModal}
        title={modalData.title}
        url={modalData.url}
        partner={modalData.partner}
      />
      
      <EnhancedBookingModal
        isOpen={enhancedModalData.isOpen}
        onClose={closeEnhancedBookingModal}
        bookingType={enhancedModalData.bookingType}
        game={enhancedModalData.game}
        teamName={enhancedModalData.teamName}
        userLocation={formData.travelFrom}
      />
      
      <div className="max-w-4xl mx-auto">
        <div className="card p-8 bg-white">
          {/* Header */}
          <div className="text-center mb-8">
            <div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{ 
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, color: '#ffffff', textShadow: '0 1px 1px rgba(0,0,0,0.25)'
              }}
            >
              <MapPin size={32} className="text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Plan Your {selectedTeam.name} Trip</h2>
            <p className="text-xl text-gray-600">Select a game from the schedule below to get personalized travel recommendations</p>
            
            {/* AI Status Indicator */}
            <div className="mt-4">
              {isOpenAIConfigured() ? (
                <div className="inline-flex items-center space-x-2 text-green-600 text-sm">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span>Powered by 40 Bags (Live ESPN Schedule)</span>
                </div>
              ) : (
                <div className="inline-flex items-center space-x-2 text-amber-600 text-sm">
                  <AlertCircle size={16} />
                  <span>Using sample itinerary - </span>
                  <button 
                    onClick={() => setShowAPIInstructions(!showAPIInstructions)}
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    Enable enhanced itineraries
                  </button>
                </div>
              )}
            </div>

          </div>

          {/* API Instructions Modal */}
          {showAPIInstructions && (
            <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <Key size={24} className="text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">Enable Enhanced 40 Bags Itineraries</h3>
                  <div className="text-sm text-blue-800 space-y-2">
                    <p className="font-medium">To get personalized, real-time trip plans with live schedule verification:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-4">
                      <li>Optional: If you have an OpenAI API key, get it from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">platform.openai.com</a></li>
                      <li>For Vercel: Go to your project Settings → Environment Variables</li>
                      <li>Add: <code className="bg-blue-100 px-1 rounded">REACT_APP_OPENAI_API_KEY</code> with your key</li>
                      <li>Redeploy your project</li>
                    </ol>
                    <p className="text-blue-700 mt-3">40 Bags will verify game schedules, surface venues, and assemble compelling, bookable plans tailored to your team.</p>
                  </div>
                  <button 
                    onClick={() => setShowAPIInstructions(false)}
                    className="mt-3 text-blue-600 hover:text-blue-700 text-sm underline"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Travel From Input */}
          <div className="mb-8">
            <label htmlFor="travelFrom" className="block text-sm font-semibold text-gray-700 mb-2">
              <Plane className="inline mr-2" size={16} />
              Traveling from (city or airport)
            </label>
            <input
              type="text"
              id="travelFrom"
              name="travelFrom"
              value={formData.travelFrom}
              onChange={handleInputChange}
              placeholder="e.g., Atlanta, GA"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
              style={{ 
                '--tw-ring-color': primaryColor,
                focusRingColor: primaryColor 
              }}
              required
            />
            {fromToast && (
              <div className="mt-2 p-3 rounded-lg border bg-amber-50 border-amber-200 text-amber-800 text-sm">
                {fromToast}
              </div>
            )}
          </div>

          {/* Team Schedule */}
          {scheduleLoading ? (
            <div className="mb-8 p-6 bg-gray-50 rounded-xl text-center">
              <Loader className="animate-spin mx-auto text-blue-600 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading {selectedTeam.name} Schedule</h3>
              <p className="text-gray-600">Fetching live schedule data from ESPN...</p>
            </div>
          ) : scheduleError ? (
            <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-xl text-center">
              <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-red-900 mb-2">Schedule Error</h3>
              <p className="text-red-700">{scheduleError}</p>
              <button 
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : teamSchedule && teamSchedule.schedule && teamSchedule.schedule.length > 0 ? (
            <div className="mb-8">
              
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <Calendar className="mr-2" size={20} />
                  {selectedTeam.name} Upcoming Games
                  <span className="ml-2 text-sm font-normal text-gray-600">({teamSchedule.sport})</span>
                </h3>
                <div className="flex items-center space-x-3">
                  <div className="hidden md:flex items-center space-x-2 text-green-600 text-sm">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span>Live ESPN Data</span>
                  </div>
                  {teamSchedule.fullSchedule && teamSchedule.fullSchedule.length > teamSchedule.schedule.length && (
                    <button
                      onClick={() => setShowSeason(!showSeason)}
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                    >
                      {showSeason ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <span className="ml-1">{showSeason ? 'Hide full season' : 'Show full season'}</span>
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                {(() => {
                  const gamesToShow = showSeason ? (teamSchedule.fullSchedule || teamSchedule.schedule) : teamSchedule.schedule.slice(0, 6);
                  console.log('🎮 Rendering games:', { 
                    showSeason, 
                    gamesToShowLength: gamesToShow.length, 
                    firstGame: gamesToShow[0]
                  });
                  
                  return gamesToShow;
                })().map((game, index) => (
                  <GameCard
                    key={index}
                    game={game}
                    teamColors={teamColors}
                    onPlanTrip={handlePlanTrip}
                    isExpanded={expandedGame === index}
                    onToggle={() => handleGameExpand(index)}
                    isLoading={isLoading && selectedGame === game}
                  />
                ))}
                {teamSchedule.fullSchedule && teamSchedule.fullSchedule.length > teamSchedule.schedule.length && (
                  <div className="pt-2">
                    <button
                      onClick={() => setShowSeason(!showSeason)}
                      className="w-full inline-flex items-center justify-center px-4 py-3 rounded-xl font-semibold transition-colors border border-gray-300 bg-white hover:bg-gray-50 text-gray-800"
                    >
                      {showSeason ? (
                        <>
                          <ChevronUp size={18} className="mr-2" /> Hide full season
                        </>
                      ) : (
                        <>
                          <ChevronDown size={18} className="mr-2" /> Show full season
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="mb-8 p-6 bg-gray-50 rounded-xl text-center">
              <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Upcoming Games Found</h3>
              <p className="text-gray-600">No scheduled games found for {selectedTeam.name}. The season may be over or not yet started.</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center space-x-2 text-red-700">
                <AlertCircle size={20} />
                <p>Error: {error}</p>
              </div>
            </div>
          )}

          {/* Results (hidden inline when modal is used) */}
          {false && recommendation && selectedGame && (
            <div className="border-t border-gray-200 pt-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Your GameDay Plan</h3>
                <div className="flex items-center space-x-4">
                  {(recommendation.usingMockData || recommendation.parsingError || recommendation.textFallback) && (
                    <div className="flex items-center space-x-2 text-amber-600 text-sm">
                      <AlertCircle size={16} />
                      <span>
                        {recommendation.usingMockData && "Sample data - Connect AI for real recommendations"}
                        {recommendation.parsingError && "AI response received but parsing failed"}
                        {recommendation.textFallback && "Using text fallback - check raw response"}
                      </span>
                    </div>
                  )}
                  {(recommendation.rawResponse && isOpenAIConfigured()) && (
                    <button
                      onClick={() => setShowRawResponse(!showRawResponse)}
                      className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
                    >
                      {showRawResponse ? <EyeOff size={16} /> : <Eye size={16} />}
                      <span>{showRawResponse ? 'Hide' : 'Show'} AI Response</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Schedule Verification */}
              {recommendation.scheduleVerification && (
                <div className={`mb-8 p-4 rounded-xl ${
                  recommendation.scheduleVerification.gameExists 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <h4 className={`text-lg font-semibold mb-2 ${
                    recommendation.scheduleVerification.gameExists 
                      ? 'text-green-900' 
                      : 'text-red-900'
                  }`}>
                    Schedule Verification
                  </h4>
                  <div className={`text-sm ${
                    recommendation.scheduleVerification.gameExists 
                      ? 'text-green-800' 
                      : 'text-red-800'
                  }`}>
                    {recommendation.scheduleVerification.gameExists ? (
                      <div>
                        <p>✅ Game confirmed on the schedule!</p>
                        <p><strong>Venue:</strong> {recommendation.scheduleVerification.venue}</p>
                        {recommendation.scheduleVerification.notes && (
                          <p><strong>Notes:</strong> {recommendation.scheduleVerification.notes}</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p>❌ Game not found on {selectedGame.date}</p>
                        {recommendation.scheduleVerification.actualDate && (
                          <p><strong>Correct Date:</strong> {recommendation.scheduleVerification.actualDate}</p>
                        )}
                        <p>{recommendation.scheduleVerification.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Raw AI Response (Debug) */}
              {showRawResponse && recommendation.rawResponse && (
                <div className="mb-8 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Raw AI Response:</h4>
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap max-h-64 overflow-y-auto">
                    {recommendation.rawResponse}
                  </pre>
                </div>
              )}
              
              {/* Game Info */}
              <div className="mb-8 p-6 bg-gray-50 rounded-xl">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Game Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Matchup:</span>
                    <p className="font-medium">{recommendation.gameInfo.matchup}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Date:</span>
                    <p className="font-medium">{recommendation.gameInfo.date}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Venue:</span>
                    <p className="font-medium">{recommendation.gameInfo.venue}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Kickoff:</span>
                    <p className="font-medium">{recommendation.gameInfo.kickoff}</p>
                  </div>
                </div>
                {recommendation.gameInfo.atmosphere && (
                  <div className="mt-4 p-4 bg-white rounded-lg">
                    <span className="text-gray-600 text-sm">What Makes This Special:</span>
                    <p className="text-gray-800 mt-1">{recommendation.gameInfo.atmosphere}</p>
                  </div>
                )}
              </div>

              {/* Tickets */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="mr-2" size={20} />
                  Get Your Tickets
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recommendation.tickets.map((ticket, index) => (
                    <button
                      key={index}
                      onClick={() => openEnhancedBookingModal('tickets', selectedGame, selectedTeam.name)}
                      className="block w-full text-left p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{ticket.suggestion}</p>
                          <p className="text-sm text-gray-600">Smart search with game details</p>
                          {ticket.details && (
                            <p className="text-xs text-gray-500 mt-1">Pre-filled search for {selectedTeam.name} vs {selectedGame.opponent}</p>
                          )}
                        </div>
                        <div className="text-primary-600">🎫</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Travel */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Plane className="mr-2" size={20} />
                  Travel & Flights
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recommendation.travel.flights.map((flight, index) => (
                    <button
                      key={index}
                      onClick={() => openEnhancedBookingModal('flights', selectedGame, selectedTeam.name)}
                      className="block w-full text-left p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{flight.suggestion}</p>
                          <p className="text-sm text-gray-600">Smart flight search</p>
                          {flight.details && (
                            <p className="text-xs text-gray-500 mt-1">Pre-filled with game dates and location</p>
                          )}
                        </div>
                        <div className="text-primary-600">✈️</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Hotels - Enhanced with atmosphere */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Hotel className="mr-2" size={20} />
                  Where to Stay
                </h4>
                <div className="space-y-6">
                  {recommendation.travel.hotels.map((hotel, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl p-6 bg-gradient-to-r from-gray-50 to-white">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h5 className="font-bold text-gray-900 mb-2 text-lg">{hotel.name}</h5>
                          <p className="text-gray-700 mb-3 leading-relaxed">{hotel.suggestion}</p>
                          {hotel.atmosphere && (
                            <div className="mb-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                              <p className="text-blue-800 text-sm font-medium">The Vibe: {hotel.atmosphere}</p>
                            </div>
                          )}
                          {hotel.details && (
                            <p className="text-sm text-gray-600 mb-4">{hotel.details}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => openEnhancedBookingModal('hotels', selectedGame, selectedTeam.name)}
                        className="w-full p-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium"
                      >
                        Book Smart Hotel Search 🏨
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dining - Enhanced with descriptions */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Utensils className="mr-2" size={20} />
                  Where to Eat & Drink
                </h4>
                <div className="space-y-6">
                  {recommendation.dining.map((category, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl p-6 bg-gradient-to-r from-orange-50 to-yellow-50">
                      <h5 className="font-bold text-gray-900 mb-2 text-lg">{category.category}</h5>
                      {category.description && (
                        <p className="text-gray-700 mb-4 leading-relaxed italic">{category.description}</p>
                      )}
                      <div className="space-y-3">
                        {category.venues.map((venue, venueIndex) => (
                          <div key={venueIndex} className="flex items-start p-4 bg-white rounded-lg border border-orange-200">
                            <span className="w-3 h-3 bg-orange-500 rounded-full mt-2 mr-4 flex-shrink-0"></span>
                            <p className="text-gray-800 leading-relaxed">{venue}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Restaurant & Car Rental Booking */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <button
                    onClick={() => {
                      // Open Google Maps directly for restaurant bookings using the proper URL generator
                      const restaurantUrl = generateRestaurantUrl(selectedGame);
                      window.open(restaurantUrl, '_blank', 'noopener,noreferrer');
                    }}
                    className="p-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-200 font-medium"
                  >
                    🍽️ Make Restaurant Reservations
                  </button>
                  <button
                    onClick={() => openEnhancedBookingModal('rentalCars', selectedGame, selectedTeam.name)}
                    className="p-4 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-xl hover:from-green-600 hover:to-blue-600 transition-all duration-200 font-medium"
                  >
                    🚗 Book Rental Car
                  </button>
                </div>
              </div>

              {/* AI Note */}
              {recommendation.aiNote && (
                <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-blue-800 text-sm">{recommendation.aiNote}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <TabbedItineraryModal
        isOpen={showTabbedModal}
        onClose={() => setShowTabbedModal(false)}
        recommendation={recommendation}
        game={selectedGame}
        teamName={selectedTeam?.name}
        teamColors={teamColors}
        userLocation={formData.travelFrom}
        onOpenBooking={null}
      />

      {/* Global Loading Overlay for Trip Planner */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Analyzing your trip…</h3>
              <span className="text-sm text-gray-500">~30 seconds</span>
            </div>

            <p className="text-gray-600 mb-6">Finding flights, hotels, and seats near the action. ~30 seconds.</p>

            <div className="flex items-center gap-6 mb-6">
              {/* Progress Ring */}
              <div style={{ width: 88, height: 88, position: 'relative' }} aria-label="progress">
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    background: `conic-gradient(${primaryColor} ${Math.floor(loadingProgress * 360)}deg, #e5e7eb 0deg)`
                  }}
                />
                <div style={{ position: 'absolute', inset: 8, borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="font-mono text-lg text-gray-800">
                    {`00:${String(loadingSecondsLeft).padStart(2, '0')}`}
                  </span>
                </div>
              </div>

              <div className="flex-1">
                <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                  <div className="h-full" style={{ width: `${Math.floor(loadingProgress * 100)}%`, background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }} />
                </div>
                <div className="mt-2 text-xs text-gray-500">Compiling itinerary, verifying schedule, and preparing smart booking links…</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SportsAssistant; 